import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from 'src/components/layout/page-header'
import { MainContent } from 'src/components/layout/main-content'
import { ContentPane } from 'src/components/layout/content-pane'
import { Spinner } from 'src/components/spinner'
import {
  notificationPreferenceQueries,
  updatePreferenceToggle,
  getChannelAvailability,
} from 'src/services/notification-preferences'
import type {
  NotificationPreference,
  ChannelKey,
  UnavailableReason,
} from 'src/services/notification-preferences'
import { useUser } from 'src/services/user/hooks'
import { useOrgFeatureConfig } from 'src/hooks/use-org-feature-config'
import { cn } from 'src/utils/style'

const CHANNEL_KEYS = [
  { key: 'in_app' as const, i18nKey: 'notificationPreferences.inApp' },
  { key: 'email' as const, i18nKey: 'notificationPreferences.email' },
  { key: 'whatsapp' as const, i18nKey: 'notificationPreferences.whatsApp' },
]

const unavailableReasonI18nKey: Record<UnavailableReason, string> = {
  org: 'notificationPreferences.orgDisabledChannel',
  phone: 'notificationPreferences.requiresPhone',
}

/** Maps channel API key to the camelCase field on the preference object. */
const channelFieldMap: Record<ChannelKey, keyof NotificationPreference> = {
  in_app: 'inApp',
  email: 'email',
  whatsapp: 'whatsapp',
}

function Toggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean
  disabled?: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn([
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
        checked ? 'bg-primary' : 'bg-gray-300',
        disabled && 'cursor-not-allowed opacity-50',
      ])}
    >
      <span
        className={cn([
          'pointer-events-none inline-block h-5 w-5 rounded-full bg-surface shadow ring-0 transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0',
        ])}
      />
    </button>
  )
}

export const NotificationPreferences = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: preferences, isLoading } = useQuery(notificationPreferenceQueries.list())
  const { data: user } = useUser()

  const hasPhoneNumber = Boolean(user?.phoneNumber)
  const { data: featureConfig } = useOrgFeatureConfig()

  const grouped: Map<string, NotificationPreference[]> = useMemo(() => {
    if (!preferences) return new Map<string, NotificationPreference[]>()
    const map = new Map<string, NotificationPreference[]>()
    for (const pref of preferences) {
      const group = map.get(pref.category) ?? []
      group.push(pref)
      map.set(pref.category, group)
    }
    return map
  }, [preferences])

  const toggleMutation = useMutation({
    mutationFn: (vars: { eventType: string; channel: ChannelKey; enabled: boolean }) =>
      updatePreferenceToggle(vars),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: notificationPreferenceQueries.all() })
      const previous = queryClient.getQueryData(notificationPreferenceQueries.list().queryKey)

      queryClient.setQueryData(
        notificationPreferenceQueries.list().queryKey,
        (old: NotificationPreference[] | undefined) =>
          old?.map((p) => {
            if (p.eventType !== vars.eventType) return p
            return { ...p, [channelFieldMap[vars.channel]]: vars.enabled }
          }),
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(notificationPreferenceQueries.list().queryKey, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationPreferenceQueries.all() })
    },
  })

  if (isLoading) {
    return (
      <>
        <PageHeader title={t('notificationPreferences.title')} />
        <MainContent>
          <ContentPane>
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          </ContentPane>
        </MainContent>
      </>
    )
  }

  return (
    <>
      <PageHeader title={t('notificationPreferences.title')} />
      <MainContent>
        <ContentPane>
          <div className="rounded-lg border border-border bg-surface p-6 shadow-sm">
            <p className="mb-6 text-sm text-text-placeholder">
              {t('notificationPreferences.description')}
            </p>

            {/* Table header */}
            <div className="mb-2 hidden grid-cols-[1fr_repeat(3,80px)] items-center gap-4 px-4 sm:grid">
              <span className="text-sm font-medium text-text-placeholder">
                {t('notificationPreferences.eventColumn')}
              </span>
              {CHANNEL_KEYS.map((ch) => (
                <span
                  key={ch.key}
                  className="text-center text-sm font-medium text-text-placeholder"
                >
                  {t(ch.i18nKey)}
                </span>
              ))}
            </div>

            {/* Groups */}
            {Array.from(grouped.entries()).map(([category, prefs]) => (
              <div key={category} className="mb-6 last:mb-0">
                <h3 className="mb-2 border-b border-border pb-1 text-sm font-semibold uppercase tracking-wide text-gray-700">
                  {category}
                </h3>
                <div className="space-y-2">
                  {prefs.map((pref) => (
                    <div
                      key={pref.id}
                      className="grid grid-cols-1 items-center gap-4 rounded-lg px-4 py-3 hover:bg-surface-hover sm:grid-cols-[1fr_repeat(3,80px)]"
                    >
                      <span className="text-sm font-medium text-text-primary">
                        {pref.eventLabel}
                      </span>
                      {CHANNEL_KEYS.map((ch) => {
                        const availability = getChannelAvailability({
                          channel: ch.key,
                          featureConfig,
                          hasPhoneNumber,
                        })

                        return (
                          <div key={ch.key} className="flex items-center gap-2 sm:justify-center">
                            <span className="text-xs text-text-placeholder sm:hidden">
                              {t(ch.i18nKey)}
                            </span>
                            <div className="flex flex-col items-center">
                              {availability.available ? (
                                <Toggle
                                  checked={pref[channelFieldMap[ch.key]] as boolean}
                                  onChange={(enabled) =>
                                    toggleMutation.mutate({
                                      eventType: pref.eventType,
                                      channel: ch.key,
                                      enabled,
                                    })
                                  }
                                />
                              ) : (
                                <span
                                  data-testid={`channel-unavailable-${ch.key}`}
                                  className="text-[10px] font-medium uppercase tracking-wide text-text-tertiary"
                                >
                                  {t('notificationPreferences.unavailable')}
                                </span>
                              )}
                              {!availability.available && (
                                <span className="mt-1 text-[10px] leading-tight text-text-tertiary">
                                  {t(unavailableReasonI18nKey[availability.reason])}
                                </span>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {grouped.size === 0 && (
              <div className="py-8 text-center text-sm text-text-tertiary">
                {t('notificationPreferences.emptyState')}
              </div>
            )}
          </div>
        </ContentPane>
      </MainContent>
    </>
  )
}
